import React from 'react';
import { Link } from 'react-router-dom';
import { formatUTCDate } from '../../utils/utils';
import StatusPill from '../users/StatusPill';
import UserDetailLink from '../common/UserDetailLink';

/**
 * After-signup survey list table (069). Same au-table styling as every other admin list.
 *
 * The four derived columns are the ones worth scanning across members: how setup went,
 * whether they understand the app, whether they intend to stay, and — the actionable one —
 * who asked us to get in touch. That last one is a badge, not prose, because it is the
 * column an operator is looking FOR rather than reading.
 *
 * `Free trial granted` carries the length of the LATEST grant with it — "Yes - 15 Days",
 * "Yes - Lifetime" — worded by the API so both survey tables read identically, and a bare
 * "Yes" when we have no length for it. It named every grant the member had ever had for a
 * few weeks; the column is about where they stand now, and the full history is on
 * /free-grants.
 *
 * **View** is a column of its own at the end of the row, linking to this survey's own response
 * page (`/signup-survey-response/:id`). It is a real <Link>, so it is middle-clickable and
 * copyable. The member's name links to their User 360 page (`/user-detail/:encodedUserId`),
 * styled as a link rather than a disguised control; with no member behind the response it
 * stays plain text.
 *
 * `Subscription` is the /users status pill — the SAME component, reading the same
 * `subscription_state` / `trial_days_remaining` the members list reads. Both survey tabs
 * carry it because it describes the responder, not the questionnaire, and it is the fact an
 * operator checks before granting the fifteen days this survey promised.
 *
 * `Grant Free Access` is the action those two columns lead to (065). This survey promises the
 * responder fifteen days and nothing applies it automatically, so the screen that shows who has
 * been paid is also the screen that pays them. `onGrantAccess` is optional — without it the
 * column is not rendered at all.
 */
export default function SignupSurveyTable({ items, onGrantAccess }) {
  const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);

  // The stored value is the option LABEL ("Yes, please!" / "No, I'm all set for now").
  const askedForSupport = (v) => /^yes/i.test(String(v || ''));

  // Whether we ever granted this member free access. Arrives as a PG boolean; tolerate the
  // 1/0 and 't' shapes too. Every grant counts, revoked and expired included — the same
  // question the /users "Last provided" cell answers.
  const granted = (it) => {
    const v = it.free_access_granted;
    return v === true || v === 1 || v === '1' || v === 't' || v === 'true';
  };

  // "Yes - 15 Days" — the LATEST grant only, inside the badge rather than in a column of its
  // own. It briefly named every reward the member had ever had ("Yes - 15 Days, 90 Days"); a
  // column asked to say what a member is on now was answering with their history, and the
  // oldest grant read as current to anyone scanning the list. `free_access_duration` is the
  // most recent grant by `created_at DESC, id DESC` — the same row `free_access_granted_at`
  // dates, so the length and the date in the next column always describe one grant.
  //
  // NOT `free_access_duration_all`: it is still on the API and in the CSV, and the whole
  // history is read on /free-grants. Falling back to it here would print an older length
  // under a column that promises the newest.
  //
  // A grant we know no length for stays a bare "Yes"; it is never padded with a made-up one.
  const grantedLabel = (it) => (it.free_access_duration ? `Yes - ${it.free_access_duration}` : 'Yes');

  return (
    <div className="au-table-wrap">
      <table className="au-table sr-responses-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Email</th>
            <th>Submitted</th>
            <th>Subscription</th>
            <th>Free trial granted</th>
            <th>Trial provided on</th>
            {onGrantAccess && <th>Grant Free Access</th>}
            <th>Setup ease</th>
            <th>Understanding</th>
            <th>Keep using</th>
            <th>Wants help</th>
            <th>View</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.response_id}>
              <td>
                {it.user_id ? (
                  <Link
                    className="au-link"
                    to={`/user-detail/${btoa(String(it.user_id))}`}
                    title="Open user profile"
                    data-testid="user-detail-link"
                  >
                    {it.name || `User #${it.user_id}`}
                  </Link>
                ) : (
                  <span className="sr-user">{it.name || '—'}</span>
                )}
              </td>
              <td><UserDetailLink userId={it.user_id} fallback={dash(it.email)} testId="user-detail-email-link">{it.email}</UserDetailLink></td>
              <td>{formatUTCDate(it.submitted_at)}</td>
              <td>
                <StatusPill state={it.subscription_state} trialDays={it.trial_days_remaining} />
              </td>
              <td>
                <span className={`sr-grant ${granted(it) ? 'sr-grant--yes' : 'sr-grant--no'}`}>
                  {granted(it) ? grantedLabel(it) : 'No'}
                </span>
              </td>
              <td>{it.free_access_granted_at ? formatUTCDate(it.free_access_granted_at) : '—'}</td>
              {onGrantAccess && (
                <td>
                  {/* No member behind the response, nobody to grant. The other columns still
                      render — the answers are worth reading either way. */}
                  {it.user_id ? (
                    <button
                      type="button"
                      className="fg-row-action"
                      onClick={() => onGrantAccess(it)}
                      data-testid="grant-free-access"
                    >
                      Grant Free Access
                    </button>
                  ) : '—'}
                </td>
              )}
              <td className="tnum">{it.setup_ease ? `${it.setup_ease} / 5` : '—'}</td>
              <td>{dash(it.understanding)}</td>
              <td className="tnum">{it.keep_using ? `${it.keep_using} / 10` : '—'}</td>
              <td>
                {it.support
                  ? <span className={`ss-flag ${askedForSupport(it.support) ? 'ss-flag--yes' : 'ss-flag--no'}`}>
                      {askedForSupport(it.support) ? 'Yes' : 'No'}
                    </span>
                  : '—'}
              </td>
              <td>
                <Link
                  className="sr-view-btn"
                  to={`/signup-survey-response/${it.response_id}`}
                  data-testid="view-response"
                >
                  View
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
